import {
  ActionIcon,
  Autocomplete,
  Badge,
  Button,
  Drawer,
  Flex,
  Loader,
  Pagination,
  ScrollArea,
  Skeleton,
  Stack,
  Table,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { IconEdit, IconPlus, IconRefresh, IconX } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import exportFromJSON from 'export-from-json';
import React, { useState } from 'react';
import COLORS from '../../../constants/colors';
import { fetchDistributor } from '../../../services/distributor';
import {
  handleErrorResponse,
  isArrayAndHasContent,
  isObjectAndHasProperties,
} from '../../../utils/utils';
import DistributorForm from '../../Distributor/DistributorForm';
import ExportCsvXls from '../../Global/ExportCsvXlsx';
import NoDataPlaceholder from '../../Global/NoDataPlaceholder';
import SearchInput from '../../Global/SearchInput';

const DistributorsTab = ({ regionPagelessdata, areaPagelessData }) => {
  const [page, setPage] = useState(1);
  const [searchKey, setSearchKey] = useState(null);

  const [searchByRegion, setSearchByRegion] = useState(null);
  const [regionSearchKey, setRegionSearchKey] = useState(null);
  const [searchByArea, setSearchByArea] = useState(null);
  const [areaSearchKey, setAreaSearchKey] = useState(null);

  const [invokingRefreshForSearchInput, setInvokingRefreshForSearchInput] =
    useState(null);

  const [addDistributorDrawer, setAddDistributorDrawer] = useState(false);

  const [selectedDistributor, setSelectedDistributor] = useState(null);

  const [exportType, setExportType] = useState('csv');
  const [exportToggler, setExportToggler] = useState(false);

  const { data, isLoading, isFetching, refetch, error } = useQuery({
    queryKey: [
      'distributor',
      page,
      searchKey,
      searchByRegion,
      searchByArea,
      null,
    ],
    queryFn: fetchDistributor,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    retry: false,
    onError: (error) => {
      handleErrorResponse(error);
    },
  });

  const {
    isFetching: distributorPagelessFetching,
    refetch: distributorPagelessRefetch,
  } = useQuery({
    queryKey: [
      'distributor-pageless',
      page,
      searchKey,
      searchByRegion,
      searchByArea,
      true,
    ],
    queryFn: fetchDistributor,
    refetchOnWindowFocus: false,
    enabled: exportToggler,
    keepPreviousData: true,
    retry: false,
    onError: (error) => {
      handleErrorResponse(error);
    },
    onSuccess: (response) => {
      const fileName = `DISTRIBUTORS-${dayjs(new Date()).format('DD-MM-YYYY')}`;
      const data = response.data.data.distributors.map((distributor) => {
        return {
          Name: distributor.name,
          Status: distributor.isActive ? 'Active' : 'Inactive',
          Region: distributor.regionData?.name,
          Area: distributor.areaData?.name,
          CreatedAt: dayjs(distributor.createdAt).format('MMM DD, YYYY'),
        };
      });
      exportFromJSON({ data, fileName, exportType });
      setExportToggler(false);
    },
  });

  if (isLoading)
    return (
      <div>
        <Skeleton height="80vh" mt={10} />
      </div>
    );

  if (error)
    return (
      <div>
        <NoDataPlaceholder apiError={true} />
      </div>
    );

  const handleSearch = (value) => {
    setPage(1);
    setSearchKey(value.trim());
  };

  const handleRefresh = () => {
    setPage(1);
    setInvokingRefreshForSearchInput(!invokingRefreshForSearchInput);
    setSearchKey('');
  };

  const exportData = () => {
    if (!exportToggler) {
      setExportToggler(true);
      distributorPagelessRefetch();
    } else {
      distributorPagelessRefetch();
    }
  };

  const { distributors, total } = data.data.data;

  return (
    <div>
      {/* CUSTOM MODAL AND DRAWER START */}
      <Drawer
        opened={addDistributorDrawer}
        onClose={() => {
          setSelectedDistributor(null);
          setAddDistributorDrawer(false);
        }}
        title={
          isObjectAndHasProperties(selectedDistributor)
            ? 'Edit Distributor'
            : 'Add Distributor'
        }
        position="right">
        <DistributorForm
          update={isObjectAndHasProperties(selectedDistributor)}
          defaultValues={selectedDistributor}
          onUpdate={() => {
            refetch();
            setAddDistributorDrawer(false);
          }}
          regionPagelessdata={regionPagelessdata}
          areaPagelessData={areaPagelessData}
        />
      </Drawer>

      {/* CUSTOM MODAL AND DRAWER END */}

      <Stack>
        <Flex justify="space-between" align="center" py="sm">
          <Flex gap={20}>
            <div>
              <SearchInput
                handleRefresh={handleRefresh}
                handleSearch={handleSearch}
                placeholder="Search Distributor"
                invokeRefresh={invokingRefreshForSearchInput}
                refreshBtn={false}
              />
            </div>
            <Autocomplete
              placeholder="Search by region"
              onChange={(value) => {
                if (regionSearchKey && regionSearchKey.length === 0) {
                  setRegionSearchKey(null);
                } else {
                  setRegionSearchKey(value);
                }
              }}
              limit={regionPagelessdata?.length || 0}
              maxDropdownHeight="200px"
              dropdownPosition="bottom"
              nothingFound="No options"
              value={regionSearchKey || ''}
              onItemSubmit={(e) => {
                setSearchByRegion(e.id);
              }}
              rightSection={
                searchByRegion ? (
                  <UnstyledButton
                    sx={{
                      justifyContent: 'center',
                      alignItems: 'center',
                      display: 'flex',
                    }}
                    onClick={() => {
                      setRegionSearchKey(null);
                      setSearchByRegion(null);
                    }}>
                    <IconX size={15} />
                  </UnstyledButton>
                ) : null
              }
              data={
                regionPagelessdata?.map((location) => ({
                  id: location.id,
                  value: location.name,
                })) || []
              }
            />
            <Autocomplete
              placeholder="Search by area"
              onChange={(value) => {
                if (areaSearchKey && areaSearchKey.length === 0) {
                  setAreaSearchKey(null);
                } else {
                  setAreaSearchKey(value);
                }
              }}
              limit={areaPagelessData?.length || 0}
              maxDropdownHeight="200px"
              dropdownPosition="bottom"
              nothingFound="No options"
              value={areaSearchKey || ''}
              onItemSubmit={(e) => {
                setSearchByArea(e.id);
              }}
              rightSection={
                searchByArea ? (
                  <UnstyledButton
                    sx={{
                      justifyContent: 'center',
                      alignItems: 'center',
                      display: 'flex',
                    }}
                    onClick={() => {
                      setAreaSearchKey(null);
                      setSearchByArea(null);
                    }}>
                    <IconX size={15} />
                  </UnstyledButton>
                ) : null
              }
              data={
                areaPagelessData?.map((location) => ({
                  id: location.id,
                  value: location.name,
                })) || []
              }
            />

            <Flex gap={20} align="center" justify="center">
              <Tooltip label="Refresh">
                <ActionIcon
                  size="lg"
                  onClick={() => {
                    handleRefresh();
                    setSearchByArea(null);
                    setSearchByRegion(null);
                    setRegionSearchKey(null);
                    setAreaSearchKey(null);
                  }}
                  sx={{
                    backgroundColor: COLORS.orange,
                  }}
                  variant="filled">
                  <IconRefresh size={18} />
                </ActionIcon>
              </Tooltip>
            </Flex>
          </Flex>
          <div>
            <Button
              sx={{
                backgroundColor: COLORS.primary,
              }}
              onClick={() => setAddDistributorDrawer(true)}
              leftIcon={<IconPlus />}>
              Add Distributor
            </Button>
          </div>
        </Flex>
        {isFetching ? (
          <Stack
            sx={{
              minHeight: '50vh',
            }}
            justify="center"
            align="center">
            <Loader size="xl" />
          </Stack>
        ) : (
          <Stack
            sx={{
              minHeight: '50vh',
            }}>
            {isArrayAndHasContent(distributors) ? (
              <ScrollArea h="60vh">
                <Table highlightOnHover>
                  <thead
                    style={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                      backgroundColor: 'white',
                    }}>
                    <tr>
                      <th>Name</th>
                      <th>Region</th>
                      <th>Area</th>
                      <th>Status</th>
                      <th>Created At</th>
                      <th>Created By</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {distributors.map((distributor, index) => (
                      <tr key={index}>
                        <td
                          style={{
                            maxWidth: '120px',
                          }}>
                          {distributor?.name}
                        </td>
                        <td>{distributor?.regionData?.name}</td>
                        <td>{distributor?.areaData?.name}</td>

                        <td>
                          <Badge
                            color={distributor?.isActive ? 'green' : 'red'}>
                            {distributor?.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td>
                          {dayjs(distributor?.createdAt).format('MMM DD, YYYY')}
                        </td>
                        <td>{distributor?.createdByUser?.name}</td>
                        <td>
                          <Flex gap={5} align="center">
                            <Tooltip label={'Edit location'}>
                              <ActionIcon
                                variant="light"
                                color="teal"
                                onClick={() => {
                                  setSelectedDistributor(distributor);
                                  setAddDistributorDrawer(true);
                                }}>
                                <IconEdit size={16} />
                              </ActionIcon>
                            </Tooltip>
                          </Flex>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </ScrollArea>
            ) : (
              <div>
                <NoDataPlaceholder
                  title="No distributor found"
                  subtext={' '}
                  //   icon={<IconMapPinOff size={70} color="#4562f7" />}
                />
              </div>
            )}
            <Flex justify="space-between">
              <Stack justify="end">
                <ExportCsvXls
                  setExportType={setExportType}
                  exportData={exportData}
                  btnLoader={distributorPagelessFetching}
                />
              </Stack>
              <Pagination
                mt="20px"
                value={page}
                onChange={setPage}
                total={Math.ceil(total / 10)}
              />
            </Flex>
          </Stack>
        )}
      </Stack>
    </div>
  );
};

export default DistributorsTab;
